import { createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery, useMutation } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  PlusIcon,
  XIcon,
  TrashIcon,
  KeyIcon,
  PaletteIcon,
  ShieldCheckIcon,
  LinkIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import { trpc } from '@/main'
// Same shape as beta.generate: this route IS the app-settings form and `UpdateAppSchema` is its validator,
// so zod is on its critical path by definition.
// oxlint-disable-next-line shared-barrel/no-barrel-values -- this route is the form; see above.
import { UpdateAppSchema } from 'shared'
import { useAppForm } from '@/lib/use-app-form'
import { toast } from '@/lib/toast'
import { CopyableCredential } from './-components/api/copyable-credential'
import { DarkModeSelector } from './-components/api/dark-mode-selector'
import { SectionCard } from './-components/api/section-card'
import { SettingsContentSkeleton } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/account/api/$appId/settings')({
  component: RouteComponent,
  pendingComponent: SettingsContentSkeleton,
})

function RouteComponent() {
  const { t } = useLingui()
  const { appId } = Route.useParams()
  // The parent `$appId` layout declares and warms this — context flows down, so the tab reads ITS field.
  const { data: app } = useSuspenseQuery(Route.useRouteContext().appQueryOptions)
  const { mutateAsync: updateApp } = useMutation(trpc.apps.update.mutationOptions())
  const { mutateAsync: deleteApp, isPending: isDeleting } = useMutation(trpc.apps.delete.mutationOptions())

  const form = useAppForm({
    validators: { onSubmit: UpdateAppSchema },
    defaultValues: {
      name: app.app.name ?? null,
      domain: app.app.domain ?? [],
      accentColor: app.app.accentColor ?? null,
      darkMode: app.app.darkMode ?? null,
      applicantButtonExit: app.app.applicantButtonExit ?? null,
      jobButtonExit: app.app.jobButtonExit ?? null,
      enableJobAddApplicantButton: app.app.enableJobAddApplicantButton ?? false,
    },
    onSubmit: async ({ value }) => {
      await updateApp({
        appClientId: appId,
        data: {
          ...value,
          accentColor: value.accentColor || null,
        },
      })
      toast.success(t`Settings saved successfully`)
    },
  })

  const handleAddDomain = () => {
    form.setFieldValue('domain', [...form.getFieldValue('domain'), ''])
  }

  const handleDeleteDomain = (index: number) => {
    const domains = form.getFieldValue('domain')
    form.setFieldValue(
      'domain',
      domains.filter((_, i) => i !== index),
    )
  }

  const handleDeleteApp = async () => {
    const confirmMsg = t`The application with client ID ${appId} is going to be removed.\nType "Remove application" to confirm`
    const deleteText = t`Remove application`
    if (window.prompt(confirmMsg) !== deleteText) return

    await deleteApp(appId)
    // No invalidateQueries here: the hard navigation below tears down and rebuilds the whole cache,
    // so invalidating first is pure wasted work.
    window.location.href = '/account/api'
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="flex flex-col gap-5"
    >
      {/* API Credentials */}
      <SectionCard
        icon={KeyIcon}
        title={t`API Credentials`}
        description={t`Use these credentials to authenticate your API requests. Keep your Client Secret secure.`}
      >
        <div className="flex flex-col gap-4">
          <CopyableCredential
            label={t`Client ID`}
            value={app.app.clientId}
            description={t`Public identifier for your app`}
          />
          <CopyableCredential
            label={t`Client Secret`}
            value={app.app.clientSecret}
            description={t`Keep this secret - never expose in client-side code`}
            hidden
          />

          {/* Code example hint */}
          <div className="bg-muted/20 flex flex-col gap-2 rounded-lg border border-dashed p-3">
            <p className="text-muted-foreground text-xs font-medium">{t`Authentication Example`}</p>
            <pre className="text-muted-foreground/80 overflow-x-auto text-[11px]">
              <code>{`Authorization: Bearer <your_token>`}</code>
            </pre>
          </div>
        </div>
      </SectionCard>

      {/* App Configuration */}
      <SectionCard
        icon={PaletteIcon}
        title={t`Appearance & Branding`}
        description={t`Customize how assessments appear when embedded in your application.`}
      >
        <div className="flex flex-col gap-4">
          <form.AppField name="name" children={(field) => <field.TextField label={t`App Name`} autoComplete="off" />} />

          <form.AppField
            name="accentColor"
            children={(field) => <field.ColorField label={t`Accent Color`} placeholder="#33CCAA" autoComplete="off" />}
          />

          <div className="w-fit">
            <form.AppField
              name="darkMode"
              children={(field) => (
                <DarkModeSelector value={field.state.value} onChange={(value) => field.handleChange(value)} />
              )}
            />
          </div>
        </div>
      </SectionCard>

      {/* Allowed Domains */}
      <SectionCard
        icon={ShieldCheckIcon}
        title={t`Security & Allowed Domains`}
        description={t`Restrict API access to specific domains. Leave empty to allow requests from any origin.`}
      >
        <div className="flex flex-col gap-3">
          <form.AppField
            name="domain"
            mode="array"
            children={(field) =>
              field.state.value.length === 0 ? (
                <div className="bg-muted/20 flex items-center gap-2 rounded-lg border border-dashed p-4">
                  <div className="flex size-8 items-center justify-center rounded-full bg-amber-500/10 text-amber-600">
                    <AlertTriangleIcon className="size-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{t`No domain restrictions`}</p>
                    <p className="text-muted-foreground text-xs">{t`All domains can make API requests. Add domains for production security.`}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {field.state.value.map((domain, domainIdx) => (
                    <div key={`domain-${domain || domainIdx}`} className="flex items-center gap-2">
                      <Input
                        placeholder={t`https://example.com`}
                        defaultValue={domain}
                        onChange={(e) => {
                          const newDomains = [...field.state.value]
                          newDomains[domainIdx] = e.target.value
                          field.handleChange(newDomains)
                        }}
                        className="text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t`Remove domain`}
                        onClick={() => handleDeleteDomain(domainIdx)}
                        className="text-muted-foreground hover:text-destructive shrink-0"
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )
            }
          />
          <Button type="button" variant="outline" onClick={handleAddDomain} className="w-fit">
            <PlusIcon className="size-4" />
            {t`Add Domain`}
          </Button>
        </div>
      </SectionCard>

      {/* Integration Settings */}
      <SectionCard
        icon={LinkIcon}
        title={t`Integration Callbacks`}
        description={t`Configure redirect URLs and button behavior for seamless integration.`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <form.AppField
              name="applicantButtonExit"
              children={(field) => <field.TextField label={t`Applicant Exit URL`} />}
            />
            <p className="text-muted-foreground text-[11px]">{t`Applicants will be redirected here with ?applicantId=<id> parameter`}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <form.AppField name="jobButtonExit" children={(field) => <field.TextField label={t`Job Exit URL`} />} />
            <p className="text-muted-foreground text-[11px]">{t`Job descriptions will redirect here with ?jobId=<id> parameter`}</p>
          </div>

          <div className="border-t pt-2">
            <form.AppField
              name="enableJobAddApplicantButton"
              children={(field) => (
                <field.SwitchField label={t`Allow adding applicants to jobs from the embedded interface`} />
              )}
            />
          </div>
        </div>
      </SectionCard>

      {/* Save Button */}
      <div className="flex justify-end">
        <form.AppForm>
          <form.SubmitButton label={t`Save Changes`} />
        </form.AppForm>
      </div>

      {/* Danger Zone */}
      <SectionCard
        icon={TrashIcon}
        title={t`Delete API Client`}
        description={t`Permanently remove this client and revoke all access. This cannot be undone.`}
        variant="danger"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">{t`Remove this API client`}</p>
            <p className="text-muted-foreground text-xs">{t`All integrations using these credentials will stop working.`}</p>
          </div>
          <Button type="button" variant="destructive" onClick={handleDeleteApp} disabled={isDeleting}>
            <TrashIcon className="size-4" />
            {t`Delete`}
          </Button>
        </div>
      </SectionCard>
    </form>
  )
}
